import React, { useContext, useState } from 'react';
import DOMPurify from 'dompurify';
import { useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath } from '../../i18n';
import SchemaMarkup from '../../components/SchemaMarkup/SchemaMarkup';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import { buildFAQPageSchema } from '../../utils/schemaMarkup';
import medicalFaqs from '../../data/faqs/medical-faqs-i18n.json';
import altTextData from '../../data/images/alt-text.json';
import './FAQ.css';

const BLUE_ABSTRACT_BG = '/assets/images/heroes/blue-abstract-bg.webp';

// Get localized Q&A for a FAQ item
function getLocalizedFaq(faq, locale) {
  if (locale === 'en' || !faq.translations || !faq.translations[locale]) {
    return { question: faq.question, answer: faq.answer };
  }
  return {
    question: faq.translations[locale].question || faq.question,
    answer: faq.translations[locale].answer || faq.answer,
  };
}

// Localized category labels
function categoryLabel(cat, locale) {
  const key = {
    'sports injury': 'faq.category.sportsInjury',
    'car accident': 'faq.category.carAccident',
    'general': 'faq.category.general',
    'orthopedic': 'faq.category.orthopedic',
  }[cat];
  if (key) return t(key, locale);
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

// Group FAQs by category
function groupByCategory(faqs) {
  const groups = {};
  faqs.forEach(faq => {
    const cat = faq.category || 'general';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(faq);
  });
  return groups;
}

// Clean HTML entities in text
function cleanText(text) {
  if (!text) return '';
  return text
    .replace(/&#8217;/g, "'")
    .replace(/&#8220;/g, '\u201c')
    .replace(/&#8221;/g, '\u201d')
    .replace(/&#8211;/g, '\u2013')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function FAQAccordion({ faqs, locale }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {faqs.map((item, i) => {
        const isOpen = openIdx === i;
        const localized = getLocalizedFaq(item, locale);
        return (
          <div
            key={i}
            className="glass-panel"
            style={{
              borderRadius: 10,
              overflow: 'hidden',
              border: isOpen ? '1px solid var(--color-accent)' : '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <button
              onClick={() => setOpenIdx(isOpen ? null : i)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '14px 18px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.97rem',
                color: 'var(--color-primary)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span>{cleanText(localized.question)}</span>
              <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
              <div
                style={{
                  padding: '0 18px 16px',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.93rem',
                  lineHeight: 1.75,
                  background: 'rgba(255,255,255,0.04)',
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanText(localized.answer)) }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function FAQ() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;

  const grouped = groupByCategory(medicalFaqs);
  const categoryOrder = ['car accident', 'sports injury', 'orthopedic', 'general'];
  const orderedCategories = [
    ...categoryOrder.filter(c => grouped[c]),
    ...Object.keys(grouped).filter(c => !categoryOrder.includes(c)),
  ];

  const [openCategory, setOpenCategory] = useState(orderedCategories[0] || null);

  // Build FAQ schema for SEO using English text (schema.org standard)
  const allFaqs = medicalFaqs.slice(0, 20);
  const faqPageSchema = buildFAQPageSchema(
    allFaqs.map(q => ({ question: cleanText(q.question), answer: cleanText(q.answer) }))
  );
  const schemaData = {
    name: t('faq.title', locale),
    mainEntity: allFaqs.map(q => ({
      '@type': 'Question',
      name: cleanText(q.question),
      acceptedAnswer: { '@type': 'Answer', text: cleanText(q.answer) },
    })),
  };

  const baseDomain = brand?.domain || 'gaspineortho.com';
  const faqPath = location.pathname.replace(/^\/(en|es|pt|ko|ja|fr)/, '') || '/faqs';

  return (
    <div className="faq-page">
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={faqPath} />
      {faqPageSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
        />
      )}
      <SchemaMarkup type="FAQPage" data={schemaData} />

      <section
        className="faq-hero glass-dark"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${BLUE_ABSTRACT_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        aria-label={altTextData['/assets/images/heroes/blue-abstract-bg.webp']?.[locale] || altTextData['/assets/images/heroes/blue-abstract-bg.webp']?.en || ''}
      >
        <div className="container">
          <h1>{t('faq.title', locale)}</h1>
          <p>{t('faq.subtitle', locale, { brandName: brand.name, count: medicalFaqs.length })}</p>
        </div>
      </section>

      <section className="container" style={{ maxWidth: 860, margin: '0 auto', padding: '40px 20px 60px' }}>
        {orderedCategories.map((cat) => {
          const isOpen = openCategory === cat;
          const items = grouped[cat];
          return (
            <div key={cat} style={{ marginBottom: 16 }}>
              <button
                onClick={() => setOpenCategory(isOpen ? null : cat)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '16px 22px',
                  background: isOpen
                    ? 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary, var(--color-primary)) 100%)'
                    : 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: isOpen ? '12px 12px 0 0' : 12,
                  cursor: 'pointer',
                  color: isOpen ? 'white' : 'var(--color-primary)',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.2s',
                }}
              >
                <span>{categoryLabel(cat, locale)}</span>
                <span style={{ opacity: 0.8 }}>{t('faq.questionsCount', locale, { count: items.length })} {isOpen ? '▲' : '▼'}</span>
              </button>

              {isOpen && (
                <div
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '16px',
                  }}
                >
                  <FAQAccordion faqs={items} locale={locale} />
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
